import { values } from 'lodash';
import * as yup from 'yup';
import { LinkFields, Platform, AdEnvironment, Format } from '../../types';
import { hasNoSpaces, isSimpleUrl, isUrlPath, isAppScheme, isAndroidAppScheme } from '../../helpers/utilities';

export const validationSchema = yup.object().shape<LinkFields>({
  platform: yup.mixed<Platform>().oneOf(values(Platform), 'Select a Platform').required('Select a Platform'),

  adEnvironment: yup
    .mixed<AdEnvironment>()
    .oneOf(values(AdEnvironment), 'Select an Ad Environment')
    .when('platform', (platform: Platform, fieldSchema: yup.MixedSchema) =>
      [Platform.ios, Platform.multi].includes(platform)
        ? fieldSchema.required('Select an Ad Environment')
        : fieldSchema,
    ),

  format: yup
    .mixed<Format>()
    .oneOf(values(Format), 'Select a Format')
    .when('platform', (platform: Platform, fieldSchema: yup.MixedSchema) =>
      [Platform.ios, Platform.multi].includes(platform) ? fieldSchema.required('Select a Format') : fieldSchema,
    ),

  targetApp: yup.string().required('Select an App'),
  targetTracker: yup.string().trim().required('Select a Tracker').test({
    test: hasNoSpaces,
    message: 'Select a Tracker',
  }),

  androidApp: yup
    .string()
    .when(
      ['platform', 'targetAppPlatform'],
      (platform: Platform, targetAppPlatform: Platform, fieldSchema: yup.StringSchema) =>
        platform === Platform.multi && targetAppPlatform === Platform.ios
          ? fieldSchema.required('Select an Android App')
          : fieldSchema,
    ),
  androidTracker: yup
    .string()
    .trim()
    .test({
      test: hasNoSpaces,
      message: 'Select an Android Tracker',
    })
    .when(
      ['platform', 'targetAppPlatform'],
      (platform: Platform, targetAppPlatform: Platform, fieldSchema: yup.StringSchema) =>
        platform === Platform.multi && targetAppPlatform === Platform.ios
          ? fieldSchema.required('Select an Android Tracker')
          : fieldSchema,
    ),

  rawUniversalLink: yup
    .string()
    .trim()
    .when('format', (format: Format, fieldSchema: yup.StringSchema) =>
      [Format.ulink, Format.jsr].includes(format)
        ? fieldSchema.required('Enter a valid Raw Universal Link').test({
            test: hasNoSpaces,
            message: 'Enter a valid Raw Universal Link',
          })
        : fieldSchema,
    ),

  iosAppScheme: yup
    .string()
    .trim()
    .test({
      test: isAppScheme,
      message: 'Enter a valid iOS App Scheme',
    })
    .when(['platform', 'format'], (platform: Platform, format: Format, fieldSchema: yup.StringSchema) =>
      platform === Platform.ios && format === Format.tdl
        ? fieldSchema.required('Enter a valid iOS App Scheme')
        : fieldSchema,
    ),
  androidAppScheme: yup
    .string()
    .trim()
    .test({
      test: isAndroidAppScheme,
      message: 'Enter a valid Android App Scheme',
    })
    .when(
      ['platform', 'format', 'targetAppPlatform'],
      (platform: Platform, format: Format, targetAppPlatform: Platform, fieldSchema: yup.StringSchema) =>
        platform === Platform.android ||
        (platform === Platform.multi &&
          targetAppPlatform === Platform.ios &&
          [Format.ulink, Format.jsr].includes(format))
          ? fieldSchema.required('Enter a valid Android App Scheme')
          : fieldSchema,
    ),

  commonAppScheme: yup
    .string()
    .trim()
    .test({
      test: isAppScheme,
      message: 'Enter a valid iOS/Android App Schema',
    })
    .when(['platform', 'format'], (platform: Platform, format: Format, fieldSchema: yup.StringSchema) =>
      platform === Platform.multi && format === Format.tdl
        ? fieldSchema.required('Enter a valid iOS/Android App Schema')
        : fieldSchema,
    ),

  iosUniversalLinkPath: yup
    .string()
    .trim()
    .when(
      ['platform', 'adEnvironment', 'format', 'iosUniversalLinkPathEnforce'],
      (
        platform: Platform,
        adEnvironment: AdEnvironment,
        format: Format,
        enforce: boolean,
        fieldSchema: yup.StringSchema,
      ) => {
        const message =
          format === Format.tdl ? 'Enter a valid iOS deeplink path' : 'Enter a valid iOS universal link path';
        if (
          platform === Platform.ios &&
          [AdEnvironment.organicFacebook, AdEnvironment.organicInstagram].includes(adEnvironment)
        ) {
          fieldSchema = fieldSchema.required(message);
        }
        return enforce
          ? fieldSchema
          : fieldSchema.test({
              test: isUrlPath,
              message,
            });
      },
    ),
  androidDeeplinkPath: yup
    .string()
    .trim()
    .when(
      ['androidDeeplinkPathEnforce', 'androidDeeplinkPathSameAsIos'],
      (enforce: boolean, sameAsIos: boolean, fieldSchema: yup.StringSchema) =>
        enforce || sameAsIos
          ? fieldSchema
          : fieldSchema.test({
              test: isUrlPath,
              message: 'Enter a valid Android Deeplink Path',
            }),
    ),
  commonDeeplinkPath: yup
    .string()
    .trim()
    .when(
      ['platform', 'format', 'adEnvironment', 'commonDeeplinkPathEnforce'],
      (
        platform: Platform,
        format: Format,
        adEnvironment: AdEnvironment,
        enforce: boolean,
        fieldSchema: yup.StringSchema,
      ) => {
        const message = 'Enter a valid iOS/Android Path';
        if (
          platform === Platform.multi &&
          format === Format.tdl &&
          [AdEnvironment.organicFacebook, AdEnvironment.organicInstagram].includes(adEnvironment)
        ) {
          fieldSchema = fieldSchema.required(message);
        }
        return enforce
          ? fieldSchema
          : fieldSchema.test({
              test: isUrlPath,
              message,
            });
      },
    ),

  fallbackUrl: yup.string().trim().test({
    test: isSimpleUrl,
    message: 'Enter a valid Fallback Url',
  }),
  redirectUrl: yup.string().trim().test({
    test: isSimpleUrl,
    message: 'Enter a valid Redirect Url',
  }),
  redirectIos: yup.string().trim().test({
    test: isSimpleUrl,
    message: 'Enter a valid Redirect iOS Url',
  }),
  redirectAndroid: yup.string().trim().test({
    test: isSimpleUrl,
    message: 'Enter a valid Redirect Android Url',
  }),
});
